// src/pages/StudentRegistration.jsx
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import SummaryApi from '../common/index';

const StudentRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const encodedStudentId = queryParams.get('id');
  const isEditMode = !!encodedStudentId;
  const studentId = encodedStudentId ? atob(encodedStudentId) : null;

  const [formData, setFormData] = useState({
    student_name: '',
    father_name: '',
    mother_name: '',
    date_of_birth: '',
    caste: '',
    religion: '',
    email: '',
    degree: '',
    stream: '',
    academic_year: '2026-27',
    mobile_number: '',
    alternate_mobile_number: '',
    state: '',
    district: '',
    city: '',
    village: '',
    pincode: '',
    address: '',  
    aadhar: '',
    lead_by: '',
    declaration_agreed: false,
    utr_number: '',
    transaction_id: '',
    amount: '',
    is_paid: '0',
    payment_date: '',
    payment_receipt: null,
  });

  const [loading, setLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(isEditMode);
  const [studentIdStored, setStudentIdStored] = useState(studentId);

  // Lead By searchable dropdown state
  const [leadBySearch, setLeadBySearch] = useState('');
  const [leadByOpen, setLeadByOpen] = useState(false);
  const leadByRef = useRef(null);

  // B.Tech Streams/Specializations
  const btechStreams = [
    'Computer Science and Engineering',
    'Artificial Intelligence & Machine Learning (AI & ML)',
    'Artificial Intelligence & Data Science (AI & DS)',
    'Cyber Security',
    'Quantum Computing',
    'Robotics Engineering',
    'Electronics and Communication Engineering (ECE)',
    'Civil Engineering',
    'Mechanical Engineering',
    'Mining Engineering'
  ];

  // BSc Streams
  const bscStreams = [
    'Mathematics',
    'Physics',
    'Chemistry',
    'Computer Science',
    'Statistics',
    'Biotechnology',
    'Microbiology',
    'Biochemistry'
  ];

  // BBA Streams
  const bbaStreams = [
    'General',
    'Finance',
    'Marketing',
    'Human Resources',
    'International Business',
    'Business Analytics'
  ];

  // Caste options
  const casteOptions = [
    'OC',
    'BC-A',
    'BC-B',
    'BC-C',
    'BC-D',
    'BC-E',
    'SC',
    'ST',
    'Other'
  ];

  // Religion options
  const religionOptions = [
    'Hindu',
    'Muslim',
    'Christian',
    'Sikh',
    'Buddhist',
    'Jain',
    'Other'
  ];

  // Team Leaders
  const teamLeaders = [
    { name: 'Rushya', role: 'team leader' },
    { name: 'Jaya Kumar', role: 'team leader' },
    { name: 'K.Ravi Kumar', role: 'team leader' },
    { name: 'Surya Kiran.D', role: 'team leader' },
  ];

  // Employees
  const employees = [
    { name: 'Bharathi M', role: 'employee' },
    { name: 'Kamala Priya', role: 'employee' },
    { name: 'Yaswitha', role: 'employee' },
    { name: 'Maha Lakshmi', role: 'employee' },
    { name: 'Sirisha', role: 'employee' },
    { name: 'Shivaji', role: 'employee' },
    { name: 'Durga Pavani', role: 'employee' },
    { name: 'Sravani', role: 'employee' },
    { name: 'Karuna', role: 'employee' },
    { name: 'Sudha Rani', role: 'employee' },
    { name: 'Anusha G', role: 'employee' },
    { name: 'Dhana Laxmi', role: 'employee' },
    { name: 'Samera(Digital Leads)', role: 'employee' },
    { name: 'Vara Laskshmi', role: 'employee' },
    { name: 'Navya P', role: 'employee' },
    { name: 'Manjusha', role: 'employee' },
    { name: 'Rama Lakshmi k', role: 'employee' },
    { name: 'Yamuna', role: 'employee' },
    { name: 'Swetha Reddy Ch', role: 'employee' },
    { name: 'Sukanya M', role: 'employee' },
    { name: 'Anusha I', role: 'employee' },
    { name: 'Rama Devi k', role: 'employee' },
    { name: 'Sujatha V', role: 'employee' },
    { name: 'Neelima P', role: 'employee' },
    { name: 'Harika I', role: 'employee' },
  ];

  // All options combined with group label
  const allLeadByOptions = [
    ...teamLeaders.map(p => ({ label: `${p.name} (team leader)`, group: 'Team Leaders' })),
    ...employees.map(p => ({ label: `${p.name} (employee)`, group: 'Employees' })),
    { label: 'Kranthi (consultant)', group: 'Consultants' },
  ];
  
  // Filtered options based on search
  const filteredLeadByOptions = allLeadByOptions.filter(opt =>
    opt.label.toLowerCase().includes(leadBySearch.toLowerCase())
  );

  // Group filtered results
  const groupedFiltered = filteredLeadByOptions.reduce((acc, opt) => {
    if (!acc[opt.group]) acc[opt.group] = [];
    acc[opt.group].push(opt);
    return acc;
  }, {});

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (leadByRef.current && !leadByRef.current.contains(e.target)) {
        setLeadByOpen(false);
        setLeadBySearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isEditMode && studentId) {
      fetchStudentData();
    }
  }, [isEditMode, studentId]);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      const res = await fetch(SummaryApi.getstudentByid.url, {
        method: SummaryApi.getstudentByid.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: studentId }),
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const result = await res.json();
      if (result.status === true && result.data) {
        const student = result.data;
        const formattedDob = student.date_of_birth
          ? new Date(student.date_of_birth).toISOString().split('T')[0]
          : '';
        setFormData({
          student_name: student.student_name || '',
          father_name: student.father_name || '',
          mother_name: student.mother_name || '',
          date_of_birth: formattedDob,
          caste: student.caste || '',
          religion: student.religion || '',
          email: student.email || '',
          degree: student.degree || '',
          stream: student.stream || '',
          academic_year: student.academic_year || '2026-27',
          mobile_number: student.mobile_number || '',
          address: student.address || '',
          alternate_mobile_number: student.alternate_mobile_number || '',
          state: student.state || '',
          district: student.district || '',
          city: student.city || '',
          village: student.village || '',
          pincode: student.pincode || '',
          aadhar: student.aadhar || '',
          lead_by: student.lead_by || '',
          declaration_agreed: true,
          utr_number: student.utr_number || '',
          transaction_id: student.transaction_id || '',
          amount: student.amount || '',
          is_paid: student.is_paid || '0',
          payment_date: student.payment_date || '',
          payment_receipt: null,
        });
        setIsRegistered(true);
      } else {
        toast.error(result.message || "Failed to load student data");
      }
    } catch (err) {
      console.error("Fetch student error:", err);
      toast.error("Error loading student details.");
    } finally {
      setLoading(false);
    }
  };

  const validateName = (name) => /^[A-Za-z\s]+$/.test(name);
  const validateEmail = (email) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  const validateNumbersOnly = (value) => /^\d+$/.test(value);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      if (['student_name','father_name','mother_name','district','city','village','state'].includes(name)) {
        if (value === '' || /^[A-Za-z\s]*$/.test(value)) {
          setFormData((prev) => ({ ...prev, [name]: value }));
        }
      } else if (name === 'mobile_number' || name === 'alternate_mobile_number') {
        if (value === '' || (validateNumbersOnly(value) && value.length <= 10)) {
          setFormData((prev) => ({ ...prev, [name]: value }));
        }
      } else if (name === 'aadhar') {
        if (value === '' || (validateNumbersOnly(value) && value.length <= 12)) {
          setFormData((prev) => ({ ...prev, [name]: value }));
        }
      } else if (name === 'pincode') {
        if (value === '' || (validateNumbersOnly(value) && value.length <= 6)) {
          setFormData((prev) => ({ ...prev, [name]: value }));
        }
      } else if (name === 'degree') {
        setFormData((prev) => ({ ...prev, degree: value, stream: '' }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    }
  };

  const handleNumberOnly = (e) => {
    const char = e.key;
    if (!/[0-9]/.test(char) && !['Backspace','Delete','Tab','ArrowLeft','ArrowRight'].includes(char)) {
      e.preventDefault();
    }
  };

  const getStreamOptions = () => {
    switch(formData.degree) {
      case 'B.Tech': return btechStreams;
      case 'BSc': return bscStreams;
      case 'BBA': return bbaStreams;
      default: return [];
    }
  };

  const validateForm = () => {
    if (!formData.student_name.trim()) return "Student Name is required";
    if (!validateName(formData.student_name)) return "Student Name should contain only letters and spaces";
    if (!formData.father_name.trim()) return "Father's Name is required";
    if (!validateName(formData.father_name)) return "Father's Name should contain only letters and spaces";
    if (!formData.mother_name.trim()) return "Mother's Name is required";
    if (!validateName(formData.mother_name)) return "Mother's Name should contain only letters and spaces";
    if (!formData.date_of_birth) return "Date of Birth is required";
    if (!formData.email.trim()) return "Email ID is required";
    if (!validateEmail(formData.email)) return "Please enter a valid email address";
    if (!formData.degree) return "Degree is required";
    if (!formData.stream) return "Stream is required";
    if (!formData.academic_year) return "Academic Year is required";
    if (!formData.mobile_number) return "Mobile Number is required";
    if (formData.mobile_number.length !== 10) return "Mobile Number must be exactly 10 digits";
    if (!validateNumbersOnly(formData.mobile_number)) return "Mobile Number must contain only digits";
    if (!formData.state.trim()) return "State is required";
    if (!validateName(formData.state)) return "State should contain only letters and spaces";
    if (!formData.district.trim()) return "District is required";
    if (!validateName(formData.district)) return "District should contain only letters and spaces";
    if (!formData.city.trim()) return "City is required";
    if (!validateName(formData.city)) return "City should contain only letters and spaces";
    if (!formData.village.trim()) return "Village is required";
    if (!formData.pincode.trim()) return "Pincode is required";
    if (formData.pincode.length !== 6) return "Pincode must be exactly 6 digits";
    if (!validateNumbersOnly(formData.pincode)) return "Pincode must contain only digits";
    if (!formData.aadhar) return "Aadhar Number is required";
    if (formData.aadhar.length !== 12) return "Aadhar Number must be exactly 12 digits";
    if (!validateNumbersOnly(formData.aadhar)) return "Aadhar Number must contain only digits";
    if (!formData.lead_by) return "Lead By is required";
    if (!formData.declaration_agreed) return "You must agree to the declaration";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errorMsg = validateForm();
    if (errorMsg) { toast.error(errorMsg); return; }
    setLoading(true);
    try {
      const adminId = sessionStorage.getItem('admin_id');
      if (!adminId) { toast.error("Admin not logged in. Please login again."); return; }

      const formDataToSend = new FormData();
      formDataToSend.append('student_name', formData.student_name);
      formDataToSend.append('father_name', formData.father_name);
      formDataToSend.append('mother_name', formData.mother_name);
      formDataToSend.append('date_of_birth', formData.date_of_birth);
      formDataToSend.append('caste', formData.caste);
      formDataToSend.append('religion', formData.religion);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('degree', formData.degree);
      formDataToSend.append('stream', formData.stream);
      formDataToSend.append('academic_year', formData.academic_year);
      formDataToSend.append('mobile_number', formData.mobile_number);
      formDataToSend.append('alternate_mobile_number', formData.alternate_mobile_number);
      formDataToSend.append('address', formData.address);
      formDataToSend.append('state', formData.state);
      formDataToSend.append('district', formData.district);
      formDataToSend.append('city', formData.city);
      formDataToSend.append('village', formData.village);
      formDataToSend.append('pincode', formData.pincode);
      formDataToSend.append('aadhar', formData.aadhar);
      formDataToSend.append('lead_by', formData.lead_by);
      formDataToSend.append('admin_id', adminId);
      formDataToSend.append('created_date', new Date().toISOString().slice(0, 19).replace('T', ' '));
      formDataToSend.append('is_active', '1');
      formDataToSend.append('utr_number', formData.utr_number || '');
      formDataToSend.append('transaction_id', formData.transaction_id || '');
      formDataToSend.append('amount', formData.amount || '0');
      formDataToSend.append('is_paid', formData.is_paid || '0');
      if (formData.payment_date) formDataToSend.append('payment_date', formData.payment_date);

      let res, result;

      if (!isRegistered) {
        res = await fetch(SummaryApi.studentRegistration.url, {
          method: SummaryApi.studentRegistration.method,
          body: formDataToSend,
        });
        result = await res.json();
        if (result.success || result.student_id) {
          toast.success("Student registered successfully!");
          const newStudentId = result.student_id;
          setStudentIdStored(newStudentId);
          setIsRegistered(true);
          const encodedId = btoa(newStudentId.toString());
          const encodedName = btoa(formData.student_name.trim());
          navigate(`/create-studentfee?id=${encodedId}&name=${encodedName}`);
        } else {
          toast.error(result.message || "Registration failed");
        }
      } else {
        const currentStudentId = studentIdStored || studentId;
        formDataToSend.append('student_id', currentStudentId);
        console.log("Updating student with ID:", currentStudentId);
        console.log("Form data being sent:", Object.fromEntries(formDataToSend));
        res = await fetch(SummaryApi.updatestudentbyid.url, {
          method: SummaryApi.updatestudentbyid.method,
          body: formDataToSend,
        });
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        result = await res.json();
        if (result.status === true || result.success === true) {
          toast.success(result.message || "Student updated successfully!");
          navigate("/student-report");
        } else {
          toast.error(result.message || "Update failed");
        }
      }
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Error during operation: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "block w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  const disabledInputClass = `${inputClass} bg-gray-100 cursor-not-allowed`;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="bg-white shadow-lg rounded-xl p-6 md:p-8 border border-gray-200 relative">
        <h2 className="text-3xl font-bold text-center text-blue-900 mb-10">
          {isEditMode || isRegistered ? "Edit Student Details" : "Student Admission Form"}
        </h2>

        {loading && <div className="text-center py-4">Loading...</div>}

        <form onSubmit={handleSubmit} className="space-y-12" noValidate>

          {/* PERSONAL DETAILS SECTION */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Personal Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>Student Name (As per SSC Memo) *</label>
                <input
                  name="student_name"
                  value={formData.student_name}
                  onChange={handleChange}
                  className={isEditMode ? disabledInputClass : inputClass}
                  readOnly={isEditMode}
                  placeholder="Enter student name as per SSC memo"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Father's Name *</label>
                <input name="father_name" value={formData.father_name} onChange={handleChange} className={inputClass} placeholder="Enter father's name" required />
              </div>
              <div>
                <label className={labelClass}>Mother's Name *</label>
                <input name="mother_name" value={formData.mother_name} onChange={handleChange} className={inputClass} placeholder="Enter mother's name" required />
              </div>
              <div>
                <label className={labelClass}>Date of Birth *</label>
                <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Caste </label>
                <select
                  name="caste"
                  value={formData.caste}
                  onChange={handleChange}
                  className={inputClass}
                  
                >
                  <option value="">Select Caste</option>
                  {casteOptions.map((caste) => (
                    <option key={caste} value={caste}>{caste}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Religion </label>
                <select
                  name="religion"
                  value={formData.religion}
                  onChange={handleChange}
                  className={inputClass}
                  
                >
                  <option value="">Select Religion</option>
                  {religionOptions.map((religion) => (
                    <option key={religion} value={religion}>{religion}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Email ID *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} className={isEditMode ? disabledInputClass : inputClass} readOnly={isEditMode} required placeholder="student@example.com" />
              </div>
              <div>
                <label className={labelClass}>Aadhar Number * (12 digits)</label>
                <input name="aadhar" maxLength={12} value={formData.aadhar} onChange={handleChange} onKeyPress={handleNumberOnly} className={isEditMode ? disabledInputClass : inputClass} readOnly={isEditMode} required placeholder="12 digit Aadhar number" />
              </div>
            </div>
          </section>

          {/* COURSE DETAILS SECTION - FIXED: Specialization/Branch now spans 2 columns */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Course Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className={labelClass}>Degree *</label>
                <select name="degree" value={formData.degree} onChange={handleChange} className={inputClass} required>
                  <option value="">Select Degree</option>
                  <option value="B.Tech">B.Tech</option>
                  <option value="BSc">BSc</option>
                  <option value="BBA">BBA</option>
                </select>
              </div>
              {/* Specialization/Branch field - spans 2 columns for full text visibility */}
              <div className="md:col-span-2">
                <label className={labelClass}>{formData.degree === 'B.Tech' ? 'Specialization / Branch *' : 'Stream *'}</label>
                {formData.degree && getStreamOptions().length > 0 ? (
                  <select 
                    name="stream" 
                    value={formData.stream} 
                    onChange={handleChange} 
                    className={inputClass}
                    required
                  >
                    <option value="">Select {formData.degree === 'B.Tech' ? 'Specialization' : 'Stream'}</option>
                    {getStreamOptions().map((streamOption, index) => (
                      <option key={index} value={streamOption}>{streamOption}</option>
                    ))}
                  </select>
                ) : (
                  <input type="text" name="stream" value={formData.stream} onChange={handleChange} className={inputClass} placeholder={formData.degree ? "Enter stream" : "Select degree first"} disabled={!formData.degree} required />
                )}
              </div>
              <div>
                <label className={labelClass}>Academic Year *</label>
                <select name="academic_year" value={formData.academic_year} onChange={handleChange} className={inputClass} required>
                  <option value="2026-27">2026-27</option>
                  <option value="2025-26">2025-26</option>
                  <option value="2024-25">2024-25</option>
                </select>
              </div>
            </div>
          </section>

          {/* CONTACT DETAILS SECTION */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Contact Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>Mobile Number * (10 digits)</label>
                <input type="tel" inputMode="numeric" pattern="[0-9]*" name="mobile_number" maxLength={10} value={formData.mobile_number} onChange={handleChange} onKeyPress={handleNumberOnly} className={isEditMode ? disabledInputClass : inputClass} readOnly={isEditMode} required placeholder="10 digit mobile number" />
              </div>
              <div>
                <label className={labelClass}>Parent Mobile Number (10 digits)</label>
                <input type="tel" inputMode="numeric" pattern="[0-9]*" name="alternate_mobile_number" maxLength={10} value={formData.alternate_mobile_number} onChange={handleChange} onKeyPress={handleNumberOnly} className={inputClass} placeholder="Enter Parent Mobile Number" />
              </div>
            </div>
          </section>

          {/* ADDRESS DETAILS SECTION */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Address Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className={labelClass}>Address </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className={inputClass}
                  rows={3}
                  placeholder="Enter Full Address"
                />
              </div>
              <div>
                <label className={labelClass}>State *</label>
                <input name="state" required value={formData.state} onChange={handleChange} className={inputClass} placeholder="e.g. Andhra Pradesh, Telangana" />
              </div>
              <div>
                <label className={labelClass}>District *</label>
                <input name="district" required value={formData.district} onChange={handleChange} className={inputClass} placeholder="e.g. Krishna, Guntur" />
              </div>
              <div>
                <label className={labelClass}>City *</label>
                <input name="city" required value={formData.city} onChange={handleChange} className={inputClass} placeholder="e.g. Vijayawada, Guntur" />
              </div>
              <div>
                <label className={labelClass}>Village *</label>
                <input name="village" required value={formData.village} onChange={handleChange} className={inputClass} placeholder="e.g. Benz Circle, Patamata" />
              </div>
              <div>
                <label className={labelClass}>Pincode * (6 digits)</label>
                <input name="pincode" value={formData.pincode} onChange={handleChange} onKeyPress={handleNumberOnly} className={inputClass} maxLength={6} placeholder="e.g. 520001" required />
              </div>
            </div>
          </section>

          {/* LEAD BY SECTION */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Lead By</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>Lead By *</label>

                {/* Searchable custom dropdown */}
                <div className="relative" ref={leadByRef}>

                  {/* Trigger button */}
                  <button
                    type="button"
                    onClick={() => { setLeadByOpen(prev => !prev); setLeadBySearch(''); }}
                    className={`${inputClass} text-left flex justify-between items-center ${!formData.lead_by ? 'text-gray-400' : 'text-gray-900'}`}
                  >
                    <span className="truncate">{formData.lead_by || 'Select Lead By'}</span>
                    <svg
                      className={`w-4 h-4 ml-2 shrink-0 text-gray-500 transition-transform duration-200 ${leadByOpen ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown panel */}
                  {leadByOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-xl">

                      {/* Search box */}
                      <div className="p-2 border-b border-gray-200">
                        <div className="relative">
                          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                          </svg>
                          <input
                            type="text"
                            autoFocus
                            value={leadBySearch}
                            onChange={(e) => setLeadBySearch(e.target.value)}
                            placeholder="Search by name..."
                            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                          />
                        </div>
                      </div>

                      {/* Options list */}
                      <ul className="max-h-56 overflow-y-auto py-1">
                        {Object.keys(groupedFiltered).length === 0 ? (
                          <li className="px-4 py-3 text-sm text-gray-500 text-center">No results found</li>
                        ) : (
                          Object.entries(groupedFiltered).map(([group, options]) => (
                            <li key={group}>
                              {/* Group header */}
                              <div className="px-3 py-1 text-xs font-bold text-gray-400 bg-gray-50 uppercase tracking-widest border-y border-gray-100">
                                {group}
                              </div>
                              {options.map((opt) => (
                                <button
                                  key={opt.label}
                                  type="button"
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, lead_by: opt.label }));
                                    setLeadByOpen(false);
                                    setLeadBySearch('');
                                  }}
                                  className={`w-full text-left px-4 py-2 text-sm transition-colors hover:bg-blue-50 hover:text-blue-700
                                    ${formData.lead_by === opt.label ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'}`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </li>
                          ))
                        )}
                      </ul>

                      {/* Clear selection */}
                      {formData.lead_by && (
                        <div className="border-t border-gray-200 p-2">
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, lead_by: '' }));
                              setLeadByOpen(false);
                              setLeadBySearch('');
                            }}
                            className="w-full text-xs text-red-500 hover:text-red-700 py-1 text-center transition-colors"
                          >
                            ✕ Clear selection
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected value badge */}
                {formData.lead_by && (
                  <div className="mt-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200">
                      {formData.lead_by}
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, lead_by: '' }))}
                        className="hover:text-red-500 transition-colors font-bold text-sm leading-none"
                      >
                        ×
                      </button>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* DECLARATION */}
          <section className="border-t pt-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="declaration_agreed"
                checked={formData.declaration_agreed}
                onChange={handleChange}
                className="mt-1 w-5 h-5 accent-blue-600"
                required
              />
              <span className="text-sm text-gray-700 leading-relaxed">
                I hereby declare that the information furnished by me in the admission application is true and correct to the best of my knowledge and belief.
              </span>
            </label>
          </section>

          {/* SUBMIT BUTTON */}
          <div className="text-end mt-10">
            <button
              type="submit"
              className={`px-12 py-3 rounded-lg bg-blue-700 hover:bg-blue-800 text-white font-semibold text-lg transition shadow-md ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
              disabled={loading}
            >
              {loading ? 'Processing...' : isEditMode || isRegistered ? 'UPDATE STUDENT' : 'NEXT'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default StudentRegistration;